import { FormBuilderProps } from "."
import ExpressionInput from "../ExpressionInput"
import { cn, setFieldValueSafe } from "@/lib/utils"
import { evaluateExpression } from "@/lib/code-mirror/expressions/expression-evaluator"
import React, { useEffect, useState } from "react"
import { Property, PropertySection, PropertySelect, PropertyTypeEnum, PropertyWithId, PropertyNumber } from "@/gen"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { useWatch } from "react-hook-form"
import { DateTimePicker } from "@/components/ui/datetime-picker"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { MultiCombobox } from "@/components/ui/extensions/multi-combobox"
import Combobox from "@/components/ui/combobox"
import { PhoneInput } from "@/components/ui/extensions/phone-input"
import { useQueryDynamicOptions } from "./useQueryDynamicOptions"

export interface FormBuilderPropertyProps extends FormBuilderProps {
  property: Property
}

const useShouldHide = (property: Property) => {
  const values = useWatch({})
  return property.hide_condition ? !!evaluateExpression(property.hide_condition, values) : false
}

function FormBuilderProperty({ property, form, ...props }: FormBuilderPropertyProps) {
  const [focusToggler, setFocusToggler] = useState<any>(null)
  // const values = form.getValues()
  const shouldHide = useShouldHide(property)
  const propertyWithId = property as PropertyWithId
  const name = `properties.${propertyWithId.id}`

  const value = useWatch({ name })
  const mode = useWatch({ name: `meta_properties.${propertyWithId.id}.mode` }) ?? "fixed"

  const dynamicOptionsState = useQueryDynamicOptions({
    node_type: props.node_type!,
    property_id: propertyWithId.id,
    action_id: props.action_id,
    operation_id: props.operation_id,
    depends_on: propertyWithId.dynamic_options?.depends_on,
    form,
    enabled: propertyWithId.dynamic_options?.enabled,
  })

  const options = [...((propertyWithId as any).options || []), ...(dynamicOptionsState.data?.options || [])]

  // useEffect(() => {
  //   if (propertyWithId.dynamic_options) {
  //     setDynamicOptions(propertyWithId.options)
  //   }
  // }, [propertyWithId])

  // useEffect(() => {
  // useEffect(() => {
  //   if (shouldHide) {
  //     property.properties?.forEach((p) => {
  //       form.unregister(`properties.${p.id}`)
  //     })
  //   }
  // }, [shouldHide])

  if (shouldHide) return null

  // ███████╗███████╗ ██████╗████████╗██╗ ██████╗ ███╗   ██╗
  // ██╔════╝██╔════╝██╔════╝╚══██╔══╝██║██╔═══██╗████╗  ██║
  // ███████╗█████╗  ██║        ██║   ██║██║   ██║██╔██╗ ██║
  // ╚════██║██╔══╝  ██║        ██║   ██║██║   ██║██║╚██╗██║
  // ███████║███████╗╚██████╗   ██║   ██║╚██████╔╝██║ ╚████║
  // ╚══════╝╚══════╝ ╚═════╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝
  //
  if (property.type === PropertyTypeEnum.section) {
    const sectionProperties = property as PropertySection
    return (
      <div className={cn(sectionProperties.inline && "flex flex-row gap-3 [&>*]:flex-1")}>
        {sectionProperties.properties?.map((innerProperty, index) => (
          <FormBuilderProperty {...props} property={innerProperty} form={form} key={index} node_type={props.node_type} />
        ))}
      </div>
    )
  }

  // const inputProps = form.getInputProps(name)
  // const shouldHide = property.hideFn ? property.hideFn(form.values.properties as T) : false
  // const mode = values.meta_properties?.[propertyWithId.id!]?.mode

  let propertyElement: React.ReactNode

  switch (propertyWithId.type) {
    // ███████╗███████╗██╗     ███████╗ ██████╗████████╗
    // ██╔════╝██╔════╝██║     ██╔════╝██╔════╝╚══██╔══╝
    // ███████╗█████╗  ██║     █████╗  ██║        ██║
    // ╚════██║██╔══╝  ██║     ██╔══╝  ██║        ██║
    // ███████║███████╗███████╗███████╗╚██████╗   ██║
    // ╚══════╝╚══════╝╚══════╝╚══════╝ ╚═════╝   ╚═╝
    //
    case "select":
      propertyElement = (
        <Select>
          <SelectTrigger>
            <SelectValue placeholder={propertyWithId.placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        // <Select
        //   label={propertyWithId.label}
        //   variant="filled"
        //   data={(propertyWithId as PropertySelect).options}
        //   required={propertyWithId.required}
        //   placeholder={inputProps.value || propertyWithId.placeholder}
        //   {...inputProps}
        // />
      )
      break

    // ███╗   ███╗██╗   ██╗██╗  ████████╗██╗███████╗███████╗██╗     ███████╗ ██████╗████████╗
    // ████╗ ████║██║   ██║██║  ╚══██╔══╝██║██╔════╝██╔════╝██║     ██╔════╝██╔════╝╚══██╔══╝
    // ██╔████╔██║██║   ██║██║     ██║   ██║███████╗█████╗  ██║     █████╗  ██║        ██║
    // ██║╚██╔╝██║██║   ██║██║     ██║   ██║╚════██║██╔══╝  ██║     ██╔══╝  ██║        ██║
    // ██║ ╚═╝ ██║╚██████╔╝███████╗██║   ██║███████║███████╗███████╗███████╗╚██████╗   ██║
    // ╚═╝     ╚═╝ ╚═════╝ ╚══════╝╚═╝   ╚═╝╚══════╝╚══════╝╚══════╝╚══════╝ ╚═════╝   ╚═╝
    //
    // case "multi_select":
    //   propertyElement = <MultiSelect options={(propertyWithId as any).options} />
    //   break
    case "multi_select":
      propertyElement = <MultiCombobox options={options} />
      break
    //  ██████╗ ██████╗ ███╗   ███╗██████╗  ██████╗ ██████╗  ██████╗ ██╗  ██╗
    // ██╔════╝██╔═══██╗████╗ ████║██╔══██╗██╔═══██╗██╔══██╗██╔═══██╗╚██╗██╔╝
    // ██║     ██║   ██║██╔████╔██║██████╔╝██║   ██║██████╔╝██║   ██║ ╚███╔╝
    // ██║     ██║   ██║██║╚██╔╝██║██╔══██╗██║   ██║██╔══██╗██║   ██║ ██╔██╗
    // ╚██████╗╚██████╔╝██║ ╚═╝ ██║██████╔╝╚██████╔╝██████╔╝╚██████╔╝██╔╝ ██╗
    //  ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═════╝  ╚═════╝ ╚═════╝  ╚═════╝ ╚═╝  ╚═╝
    //
    case "combobox":
      propertyElement = <Combobox fluid options={options} loading={dynamicOptionsState.isLoading} />
      break

    // ██████╗ ██╗  ██╗ ██████╗ ███╗   ██╗███████╗
    // ██╔══██╗██║  ██║██╔═══██╗████╗  ██║██╔════╝
    // ██████╔╝███████║██║   ██║██╔██╗ ██║█████╗
    // ██╔═══╝ ██╔══██║██║   ██║██║╚██╗██║██╔══╝
    // ██║     ██║  ██║╚██████╔╝██║ ╚████║███████╗
    // ╚═╝     ╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝
    //
    case "phone":
      propertyElement = <PhoneInput />
      break
    // ███╗   ██╗██╗   ██╗███╗   ███╗██████╗ ███████╗██████╗
    // ████╗  ██║██║   ██║████╗ ████║██╔══██╗██╔════╝██╔══██╗
    // ██╔██╗ ██║██║   ██║██╔████╔██║██████╔╝█████╗  ██████╔╝
    // ██║╚██╗██║██║   ██║██║╚██╔╝██║██╔══██╗██╔══╝  ██╔══██╗
    // ██║ ╚████║╚██████╔╝██║ ╚═╝ ██║██████╔╝███████╗██║  ██║
    // ╚═╝  ╚═══╝ ╚═════╝ ╚═╝     ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝
    //
    case "number":
      propertyElement = (
        <Input
          type="number"
          min={(propertyWithId as PropertyNumber).min}
          max={(propertyWithId as PropertyNumber).max}
          required={propertyWithId.required}
          placeholder={propertyWithId.placeholder}
          className="flex-1"
        />
      )
      break

    // ███████╗██╗    ██╗██╗████████╗ ██████╗██╗  ██╗
    // ██╔════╝██║    ██║██║╚══██╔══╝██╔════╝██║  ██║
    // ███████╗██║ █╗ ██║██║   ██║   ██║     ███████║
    // ╚════██║██║███╗██║██║   ██║   ██║     ██╔══██║
    // ███████║╚███╔███╔╝██║   ██║   ╚██████╗██║  ██║
    // ╚══════╝ ╚══╝╚══╝ ╚═╝   ╚═╝    ╚═════╝╚═╝  ╚═╝
    //
    case "switch":
      propertyElement = <Switch />
      break

    // TEXTAREA
    case "textarea":
      propertyElement = <Textarea />
      break

    // ████████╗███████╗██╗  ██╗████████╗
    // ╚══██╔══╝██╔════╝╚██╗██╔╝╚══██╔══╝
    //    ██║   █████╗   ╚███╔╝    ██║
    //    ██║   ██╔══╝   ██╔██╗    ██║
    //    ██║   ███████╗██╔╝ ██╗   ██║
    //    ╚═╝   ╚══════╝╚═╝  ╚═╝   ╚═╝
    //
    case "text":
      propertyElement = <Input required={propertyWithId.required} placeholder={propertyWithId.placeholder} className="flex-1" />
      break
    // ██████╗  █████╗ ████████╗███████╗████████╗██╗███╗   ███╗███████╗
    // ██╔══██╗██╔══██╗╚══██╔══╝██╔════╝╚══██╔══╝██║████╗ ████║██╔════╝
    // ██║  ██║███████║   ██║   █████╗     ██║   ██║██╔████╔██║█████╗
    // ██║  ██║██╔══██║   ██║   ██╔══╝     ██║   ██║██║╚██╔╝██║██╔══╝
    // ██████╔╝██║  ██║   ██║   ███████╗   ██║   ██║██║ ╚═╝ ██║███████╗
    // ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝   ╚═╝   ╚═╝╚═╝     ╚═╝╚══════╝
    //
    case "datetime":
      propertyElement = <DateTimePicker />
      break
    default:
      propertyElement = null
  }

  // ███████╗██╗  ██╗██████╗ ██████╗ ███████╗███████╗███████╗██╗ ██████╗ ███╗   ██╗
  // ██╔════╝╚██╗██╔╝██╔══██╗██╔══██╗██╔════╝██╔════╝██╔════╝██║██╔═══██╗████╗  ██║
  // █████╗   ╚███╔╝ ██████╔╝██████╔╝█████╗  ███████╗███████╗██║██║   ██║██╔██╗ ██║
  // ██╔══╝   ██╔██╗ ██╔═══╝ ██╔══██╗██╔══╝  ╚════██║╚════██║██║██║   ██║██║╚██╗██║
  // ███████╗██╔╝ ██╗██║     ██║  ██║███████╗███████║███████║██║╚██████╔╝██║ ╚████║
  // ╚══════╝╚═╝  ╚═╝╚═╝     ╚═╝  ╚═╝╚══════╝╚══════╝╚══════╝╚═╝ ╚═════╝ ╚═╝  ╚═══╝
  //
  return (
    <ExpressionInput
      mode={mode}
      // inputProps={inputProps}
      onChangeMode={(newMode) => {
        setFieldValueSafe(`meta_properties.${propertyWithId.id}.mode`, newMode, form)
        if (newMode === "fixed") {
          let evaluatedValue: any = evaluateExpression(String(value), {})
          if (propertyWithId.type === "number") {
            evaluatedValue = Number(evaluatedValue)
          }
          if (propertyWithId.type === "switch") {
            evaluatedValue = typeof evaluatedValue === "boolean" ? evaluatedValue : evaluatedValue === "true"
          }
          if (propertyWithId.type === "multi_select") {
            evaluatedValue = Array.isArray(evaluatedValue) ? evaluatedValue : evaluatedValue ? evaluatedValue?.split?.(",") || [] : []
          }
          if (propertyWithId.type === "text" || propertyWithId.type === "phone") {
            evaluatedValue = evaluatedValue || ""
          }
          form.setValue(`properties.${propertyWithId.id}`, evaluatedValue)
        } else {
          setFocusToggler((t) => !t)
        }
        // setTimeout(() => {
        //   form.validateField(`properties.${propertyWithId.id}`)
        // }, 100)
      }}
      formFieldProps={{
        name,
        label: propertyWithId.label,
        required: propertyWithId.required,
        description: propertyWithId.description,
        tip: propertyWithId.tip,
        valueAsNumber: propertyWithId.type === "number",
      }}
      required={propertyWithId.required}
      focusToggler={typeof focusToggler === "boolean"}
      onRemove={
        props.deleteable
          ? () => {
              const properties = { ...form.getValues("properties") }
              const meta_properties = { ...form.getValues("meta_properties") }
              delete properties[propertyWithId.id]
              delete meta_properties[propertyWithId.id]
              form.setValue(`meta_properties`, meta_properties)
              form.setValue(`properties`, properties)
            }
          : undefined
      }
    >
      {propertyElement}
    </ExpressionInput>
  )
}

export default FormBuilderProperty
